import { styled } from "goober";
import { useCallback } from "react";
import { formatBytes } from "../../lib/formatBytes";
import { Icon } from "../Icon";
import { metaJoin } from "../MetaSeparator";
import { Text } from "../Text";
import { extensionFromMime, triggerDownload } from "./download";
import { DownloadButton } from "./DownloadButton";
import { useObjectUrl } from "./useObjectUrl";
import { Tooltip } from "../Tooltip";

const Container = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const VideoEl = styled("video")`
  width: 100%;
  max-height: 70vh;
  border-radius: 12px;
  border: 0.5px solid var(--border-alpha);
  background: var(--surface-primary);
`;

const MetaRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
`;

type Props = {
  url?: string;
  contentType: string;
  fileName?: string;
};

export function Video({ url, contentType, fileName }: Props) {
  const { blobUrl, size: fileSize } = useObjectUrl(url);

  const ext = extensionFromMime(contentType);

  const downloadName = fileName ?? `video.${ext}`;
  const download = useCallback(() => {
    if (blobUrl) triggerDownload(blobUrl, downloadName);
  }, [blobUrl, downloadName]);

  if (!blobUrl) return null;

  const metaParts = [ext.toUpperCase()];
  if (fileSize != null) metaParts.push(formatBytes(fileSize));

  return (
    <Container>
      <VideoEl controls src={blobUrl} />
      <MetaRow>
        <Text variant="text-small" color="var(--text-secondary)">
          {metaJoin(metaParts)}
        </Text>
        <div style={{ marginLeft: "auto" }}>
          <Tooltip content="Download" align="center">
            <DownloadButton onPress={download} disabled={!blobUrl}>
              <Icon name="arrow-down" size={12} />
              <Text variant="text-small-md">Download</Text>
            </DownloadButton>
          </Tooltip>
        </div>
      </MetaRow>
    </Container>
  );
}
